import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { ArrowRight, Download, TrendingUp, MapPin, BarChart3, Clock } from 'lucide-react';

const fmtGHS = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1000) return `${Math.round(num / 1000)}k`;
  return String(num);
};

const fmtNum = (n) => new Intl.NumberFormat('en-GH').format(Number(n) || 0);

function Bar({ value, max, accent = 'bg-gold' }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full h-1.5 rounded-full bg-muted/40 overflow-hidden">
      <div
        className={`h-full ${accent} rounded-full transition-[width] duration-500`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function AutoTrustIndex() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    API.get('/insights/autotrust-index')
      .then(res => setData(res.data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, []);

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `autotrust-index-${data.period_label?.replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxMakeCount = Math.max(1, ...(data?.avg_price_by_make || []).map(m => m.count));
  const maxRegionActive = Math.max(1, ...(data?.regional_demand || []).map(r => r.active));
  const maxBucket = Math.max(1, ...(data?.price_distribution || []).map(b => b.count));

  return (
    <main data-testid="index-page">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase text-gold mb-3 font-medium">AutoTrust Index</p>
            <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
              Monthly market data<br className="hidden md:block" /> for Ghana&rsquo;s auto sector.
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
              A transparent snapshot of pricing, segments, and regional demand &mdash; published monthly,
              auto-generated from anonymized transactions on AutoTrust.
            </p>
          </div>
        </div>
        {!loading && data && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span>Period: <span className="font-mono-display font-semibold text-foreground" data-testid="index-period">{data.period_label}</span></span>
            <span className="text-border">·</span>
            <span>Generated: <span className="font-mono-display text-foreground">{new Date(data.generated_at).toLocaleString('en-GB')}</span></span>
            <Button variant="outline" size="sm" onClick={downloadJson} className="gap-2 ml-auto" data-testid="index-download-btn">
              <Download className="w-3.5 h-3.5" /> Download JSON
            </Button>
          </div>
        )}
      </section>

      {loading && (
        <section className="max-w-5xl mx-auto px-4 md:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[...Array(4)].map((_, i) => (<div key={i} className="h-24 rounded-xl bg-muted/30 animate-pulse" />))}
          </div>
          <div className="h-48 rounded-xl bg-muted/30 animate-pulse" />
        </section>
      )}

      {err && !loading && (
        <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 text-center text-muted-foreground">
          Could not load the index right now. Please try again shortly.
        </section>
      )}

      {!loading && data && (
        <>
          {/* Headline tiles */}
          <section className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12 border-t border-border/40 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="index-headline">
            {[
              { label: 'Active listings', value: fmtNum(data.headline.active_listings), accent: 'text-foreground' },
              { label: 'Cars sold', value: fmtNum(data.headline.sold_listings), accent: 'text-trust-green' },
              { label: 'GHS secured', value: `GHS ${fmtGHS(data.headline.total_secured_ghs)}`, accent: 'text-gold' },
              { label: 'Average sold price', value: `GHS ${fmtGHS(data.headline.average_price_ghs)}`, accent: 'text-foreground' },
            ].map(t => (
              <motion.div
                key={t.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3 }}
                className="p-5 rounded-xl border border-border/50 bg-card"
              >
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-1.5">{t.label}</p>
                <p className={`font-mono-display text-2xl md:text-3xl font-bold tracking-tight ${t.accent}`}>{t.value}</p>
              </motion.div>
            ))}
          </section>

          {/* Avg price by make */}
          <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 border-t border-border/40">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
              <BarChart3 className="w-3 h-3 text-gold" /> Pricing
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">Average price by make</h2>
            {data.avg_price_by_make.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not enough data yet — check back next month.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-5" data-testid="index-make-grid">
                {data.avg_price_by_make.map((m, i) => (
                  <div key={m.make} data-testid={`index-make-${i}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="font-medium">{m.make}</span>
                      <span className="font-mono-display text-sm">
                        <span className="text-gold font-semibold">GHS {fmtGHS(m.avg_price)}</span>
                        <span className="text-muted-foreground ml-2">· {m.count} listed</span>
                      </span>
                    </div>
                    <Bar value={m.count} max={maxMakeCount} />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Fastest selling segments */}
          <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 border-t border-border/40">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
              <Clock className="w-3 h-3 text-trust-green" /> Velocity
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">Fastest-selling segments</h2>
            {data.fastest_selling_segments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No completed sales this period.</p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="index-segments-grid">
                {data.fastest_selling_segments.map((s, i) => (
                  <div key={s.segment} className="p-5 rounded-xl border border-border/50 bg-card" data-testid={`index-segment-${i}`}>
                    <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-1.5">{s.segment}</p>
                    <p className="font-mono-display text-2xl font-bold mb-1">{s.sold} <span className="text-sm text-muted-foreground font-normal">sold</span></p>
                    {s.days_to_sell_avg !== null && (
                      <p className="text-xs text-trust-green flex items-center gap-1"><TrendingUp className="w-3 h-3" /> avg {s.days_to_sell_avg}d to sell</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Regional demand */}
          <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 border-t border-border/40">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
              <MapPin className="w-3 h-3 text-gold" /> Geography
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">Regional demand</h2>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-5" data-testid="index-region-grid">
              {data.regional_demand.map((r, i) => (
                <div key={r.location} data-testid={`index-region-${i}`}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="font-medium">{r.location}</span>
                    <span className="font-mono-display text-sm">
                      <span className="font-semibold">{r.active}</span>
                      <span className="text-muted-foreground"> active</span>
                      {r.sold > 0 && <span className="text-trust-green ml-2">· {r.sold} sold</span>}
                    </span>
                  </div>
                  <Bar value={r.active} max={maxRegionActive} accent="bg-emerald" />
                </div>
              ))}
            </div>
          </section>

          {/* Price distribution */}
          <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 border-t border-border/40">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
              <BarChart3 className="w-3 h-3 text-gold" /> Distribution
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">Listings by price band</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="index-price-distribution">
              {data.price_distribution.map((b, i) => (
                <div key={b.bucket} className="p-5 rounded-xl border border-border/50 bg-card" data-testid={`index-bucket-${i}`}>
                  <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">{b.bucket}</p>
                  <p className="font-mono-display text-2xl font-bold mb-2">{b.count}</p>
                  <Bar value={b.count} max={maxBucket} />
                </div>
              ))}
            </div>
          </section>

          {/* Methodology */}
          <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16 border-t border-border/40">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Methodology</p>
                <p className="text-sm text-muted-foreground max-w-2xl">
                  Figures are aggregated from anonymised listings and confirmed escrow transactions on AutoTrust Ghana.
                  Personally identifiable information is excluded. The index refreshes every 10 minutes and is published in
                  full at the start of each month. Reproduction permitted with attribution to <span className="text-foreground font-medium">AutoTrust Index</span>.
                </p>
              </div>
              <Link to="/press">
                <Button variant="outline" className="gap-2 whitespace-nowrap" data-testid="index-press-link">
                  Press inquiries <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
